import React, { useContext, useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import MyOrderPopup from "./MyOrderPopup";
import { UserContext } from "../utils/UserContext";

function getMonday(date) {
  const d = new Date(new Date(date).setHours(0, 0, 0, 0));
  const day = d.getDay(),
    diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
}

function getSunday(date) {
  const d = new Date(new Date(date).setHours(0, 0, 0, 0));
  const day = d.getDay(),
    diff = d.getDate() - day + (day === 0 ? -6 : 1) + 6;
  return new Date(d.setDate(diff));
}

const weekDays = [
  "Понедельник",
  "Вторник",
  "Среда",
  "Четверг",
  "Пятница",
  "Суббота",
  "Воскресенье",
];

const rubFormatter = new Intl.NumberFormat("ru-RU");
const formatCurrency = (value) => rubFormatter.format(Number(value) || 0);

const calculateOrderTotal = (order) => {
  if (!order) return 0;
  if (typeof order.total === "number") return order.total;
  if (!Array.isArray(order.meals)) return 0;
  return order.meals.reduce(
    (acc, meal) => acc + Number(meal.price || 0) * Number(meal.amount || 0),
    0
  );
};

const MyOrders = () => {
  const [orders, setOrders] = useState(null);
  const [week, setWeek] = useState([]);
  const [dateFrom, setDateFrom] = useState(getMonday(new Date()));
  const [foundActiveWeek, setFoundActiveWeek] = useState(false);
  const [dateTo, setDateTo] = useState(getSunday(new Date()));
  const [ordersByWeekDays, setOrdersByWeekDays] = useState(null);
  const [activeOrder, setActiveOrder] = useState(null);
  const [activeOrderWarning, setActiveOrderWarning] = useState(null);
  const [needUpdate, setNeedUpdate] = useState(true);
  const [copyLoading, setCopyLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyError, setCopyError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [dayLimit, setDayLimit] = useState({});

  const navigator = useNavigate();

  const userContext = useContext(UserContext);
  const placedOrders = orders || [];
  const totalPositions = placedOrders.reduce(
    (acc, order) => acc + (order?.meals?.length || 0),
    0
  );
  const totalExchanges = placedOrders.reduce(
    (acc, order) =>
      acc +
      (order?.meals?.filter((meal) => (meal.onChangeAmount || 0) > 0).length || 0),
    0
  );
  const daysWithOrders = placedOrders.filter(Boolean).length;
  const totalAmount = placedOrders.reduce(
    (acc, order) => acc + calculateOrderTotal(order),
    0
  );
  const weekRangeLabel = `${dateFrom.toLocaleDateString("ru-RU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })} — ${dateTo.toLocaleDateString("ru-RU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })}`;

  useEffect(() => {
    if (!needUpdate || isFirstLoading) {
      setIsFirstLoading(false);
      return;
    }
    setIsLoading(true);
    Promise.all([
      api.getMyOrders({ f_datef: dateFrom, f_datet: dateTo, p: 1, s: 1000 }),
      api.getMyWeek({ date: dateFrom }),
      api.getDayLimits(),
    ])
      .then(([{ orders }, { result }, limit]) => {
        setOrders(orders);
        setWeek(result);
        setDayLimit(limit);
      })
      .catch((err) => err.status === 403 && navigator("/access_denied"))
      .finally(() => {
        setNeedUpdate(false);
        setIsLoading(false);
      });
  }, [dateFrom, dateTo, needUpdate, isFirstLoading]);

  useEffect(() => {
    if (
      !week.length ||
      foundActiveWeek ||
      dateFrom.getMilliseconds() !== new Date(week[0]?.date).getMilliseconds()
    )
      return;
    if (new Date(week.filter((el) => !el.is_work)[0].date) < new Date()) {
      changeWeek("+");
    } else {
      setFoundActiveWeek(true);
    }
  }, [week]);

  useEffect(() => {
    if (!orders) return;
    const ordersByWeekDaysArray = [];
    weekDays.forEach((weekDay, index) => {
      ordersByWeekDaysArray.push({
        weekDay: weekDay,
        date: new Date(
          new Date(
            new Date(dateFrom).setDate(new Date(dateFrom).getDate() + index)
          ).setHours(0, 0, 0, 0)
        ),
        order: orders.find(
          (el) =>
            new Date(new Date(el.date).setHours(0, 0, 0, 0)).getTime() ===
            new Date(
              new Date(
                new Date(dateFrom).setDate(new Date(dateFrom).getDate() + index)
              ).setHours(0, 0, 0, 0)
            ).getTime()
        ),
      });
    });
    setOrdersByWeekDays(ordersByWeekDaysArray);
  }, [orders]);

  const changeWeek = (whereTo) => {
    if (whereTo === "+") {
      const lastWeekDay = getSunday(new Date(dateFrom));
      const firstDayNextWeek = new Date(
        new Date(lastWeekDay).setDate(lastWeekDay.getDate() + 1)
      );
      setDateFrom(getMonday(firstDayNextWeek));
      setDateTo(getSunday(firstDayNextWeek));
    } else if (whereTo === "-") {
      const lastWeekDay = getMonday(new Date(dateFrom));
      const lastDayPrevWeek = new Date(
        new Date(lastWeekDay).setDate(lastWeekDay.getDate() - 1)
      );
      setDateFrom(getMonday(lastDayPrevWeek));
      setDateTo(getSunday(lastDayPrevWeek));
    }
    setNeedUpdate(true);
  };

  const onCopy = () => {
    setCopyLoading(true);
    api
      .copyOrdersFromPrevWeek({ from: dateFrom, to: dateTo })
      .then(() => {
        setCopySuccess(true);
        setNeedUpdate(true);
      })
      .catch((err) => err.json().then(({ message }) => setCopyError(message)))
      .finally(() => setCopyLoading(false));
  };

  useEffect(() => {
    setCopySuccess(false);
    setCopyError("");
  }, [dateFrom]);

  return (
    <>
      <div className="order-hero glass-panel">
        <div className="order-hero__content">
          <div className="spark-chip">
            <i className="bi bi-egg-fried" />
            Моя еда · Неделя
          </div>
          <h1 className="fw-bold mb-1">Личная лента заказов на неделю</h1>
          <p className="page-subtitle m-0">
            Планируйте приемы пищи в два клика, копируйте удачные недели и сразу видите свободные и
            закрытые дни.
          </p>
          <div className="order-hero__actions">
            <div className="week-control elevated">
              <span
                className="icon-btn"
                onClick={() => changeWeek("-")}
                role="button"
                aria-label="Предыдущая неделя"
              >
                <i className="bi bi-chevron-left" />
              </span>
              <div className="fw-semibold week-range">
                <div>{weekRangeLabel}</div>
              </div>
              <span
                className="icon-btn"
                onClick={() => changeWeek("+")}
                role="button"
                aria-label="Следующая неделя"
              >
                <i className="bi bi-chevron-right" />
              </span>
            </div>
            <Button
              disabled={Boolean(copyLoading) || Boolean(copySuccess) || copyError !== ""}
              variant={copyError === "" ? "outline-primary" : "danger"}
              onClick={() => onCopy()}
              className="pulse-btn"
            >
              {copyLoading
                ? "Копируем неделю..."
                : copySuccess
                  ? "Готово, неделя скопирована"
                  : copyError !== ""
                    ? copyError
                    : "Скопировать с предыдущей недели"}
            </Button>
            {isLoading ? (
              <span className="micro-pill subtle">
                <i className="bi bi-activity" />
                Обновляем данные...
              </span>
            ) : (
              ""
            )}
          </div>
          {dayLimit.type === "limit" ? (
            <span className="limit-pill">
              <i className="bi bi-piggy-bank-fill" />
              Бесплатный порог — {dayLimit.limit}
            </span>
          ) : (
            ""
          )}
        </div>
        <div className="order-hero__stats">
          <div className="order-hero__stat">
            <p className="label">Позиций</p>
            <p className="value">{totalPositions}</p>
            <span className="hint">Сколько блюд в корзине недели</span>
          </div>
          <div className="order-hero__stat">
            <p className="label">Дней с заказами</p>
            <p className="value">{daysWithOrders}</p>
            <span className="hint">Включая предстоящие дни</span>
          </div>
          <div className="order-hero__stat">
            <p className="label">На обмене</p>
            <p className="value accent">{totalExchanges}</p>
            <span className="hint">Блюд, которые вы отдали в аукцион</span>
          </div>
          <div className="order-hero__stat">
            <p className="label">Итог</p>
            <p className="value">{formatCurrency(totalAmount)} ₽</p>
            <span className="hint">Стоимость заказов за неделю</span>
          </div>
        </div>
      </div>

      <div className="day-stream glass-panel">
        <div className="day-stream__head">
          <div>
            <p className="chip muted-chip mb-2">
              <i className="bi bi-calendar3" />
              Неделя: {weekRangeLabel}
            </p>
            <h4 className="m-0">Управление днями</h4>
          </div>
        </div>

        <div className="day-stream__grid">
          {ordersByWeekDays
            ? ordersByWeekDays.map((day, ind) => {
              const dayMeta = week[ind];
              const isWeekend = dayMeta ? !dayMeta.is_work : false;
              const isBlocked =
                ((dayMeta?.denyOrderApplication && dayMeta?.is_ordered) ||
                  (dayMeta?.denyOrderWeekend && !dayMeta?.is_work)) &&
                !userContext.isRoot;
              const exchangeMeals =
                day.order?.meals?.filter((meal) => (meal.onChangeAmount || 0) > 0).length || 0;
              const orderTotal = calculateOrderTotal(day.order);

              return (
                <article
                  key={`${day.weekDay}-${ind}`}
                  className={`day-card neo-card${isWeekend ? " is-off" : ""}`}
                >
                  <div className="day-card__head">
                    <div>
                      <p className="day-card__weekday">{day.weekDay}</p>
                      <p className="day-card__date">
                        {new Date(day.date).toLocaleDateString("ru-RU", {
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                      <div className="day-card__labels">
                        {dayMeta?.spday ? (
                          <span className="micro-pill">
                            <i className="bi bi-stars" />
                            {dayMeta.spday.name}
                          </span>
                        ) : (
                          ""
                        )}
                        {isWeekend ? <span className="micro-pill danger">Выходной</span> : ""}
                      </div>
                    </div>
                  </div>

                  <div className="day-card__body">
                    {day.order ? (
                      <>
                        <div className="order-headline">
                          <span
                            className={`status-pill ${day.order.status === "ordered" ? "" : "muted"}`}
                          >
                            {day.order.status === "ordered" ? "Заказ" : "Заявка"}
                          </span>
                          <div className="d-flex align-items-center">
                            <span className="micro-pill tonal">
                              {day.order.meals.length} поз. ·  <i className="bi bi-cash-stack" />
                              {" "}{formatCurrency(orderTotal)} ₽
                            </span>
                          </div>
                        </div>
                        <div className="order-list compact mb-2">
                          {day.order.meals.slice(0, 4).map((meal) => (
                            <div
                              className={`order-list__item${(meal.onChangeAmount || 0) > 0 ? " on-exchange" : ""}`}
                              key={meal._id}
                            >
                              <div className="fw-semibold order-line-name">
                                {meal.name}
                                {(meal.onChangeAmount || 0) > 0 ? (
                                  <span
                                    className="exchange-pill small-pill icon-pill ms-2"
                                    title="На аукционе"
                                  >
                                    <i className="bi bi-arrow-left-right" />
                                  </span>
                                ) : (
                                  ""
                                )}
                              </div>
                              <div className="order-line-meta">
                                <span>
                                  {meal.amount}× {meal.price} ₽
                                </span>
                              </div>
                            </div>
                          ))}
                          {day.order.meals.length > 4 ? (
                            <div className="order-list__item muted">
                              +{day.order.meals.length - 4} еще
                            </div>
                          ) : (
                            ""
                          )}
                        </div>
                        <div className="day-card__footer">
                          {exchangeMeals ? (
                            <span className="micro-pill tonal">
                              <i className="bi bi-arrow-repeat" />
                              {exchangeMeals} на обмене
                            </span>
                          ) : (
                            ""
                          )}
                          <Button
                            variant="outline-primary"
                            className="ghost-btn"
                            onClick={() => setActiveOrder(day.order._id)}
                          >
                            Подробнее <i className="bi bi-arrow-up-right ms-1" />
                          </Button>
                        </div>
                      </>
                    ) : isBlocked ? (
                      <div className="day-card__empty">
                        <p className="fw-semibold mb-1">День закрыт для заявок</p>
                        <p className="text-muted m-0">
                          {isWeekend ? "Выходной день" : "Заявка уже создана"}
                        </p>
                      </div>
                    ) : (
                      <div className="day-card__cta">
                        <div>
                          <p className="fw-semibold mb-1">Ничего не выбрано</p>
                          <p className="text-muted m-0">Выберите блюда из меню</p>
                        </div>
                        <Button
                          variant="outline-primary"
                          className="ghost-btn"
                          onClick={() => {
                            if (dayMeta?.is_ordered || !dayMeta?.is_work) {
                              setActiveOrderWarning({
                                reason: !dayMeta?.is_work
                                  ? "weekend"
                                  : dayMeta?.is_ordered
                                    ? "ordered"
                                    : "unknown",
                                activeOrder: { new: "new", date: day.date },
                              });
                            } else {
                              setActiveOrder({ new: "new", date: day.date });
                            }
                          }}
                        >
                          Создать заказ
                        </Button>
                      </div>
                    )}
                  </div>
                </article>
              );
            })
            : ""}
        </div>
      </div>

      <MyOrderPopup
        activeOrder={activeOrder}
        setActiveOrder={setActiveOrder}
        setNeedUpdate={setNeedUpdate}
        needUpdate={needUpdate}
      />
      <Modal
        show={!!activeOrderWarning}
        onHide={() => setActiveOrderWarning(null)}
      >
        <Modal.Header closeButton>{`${activeOrderWarning?.reason === "weekend"
            ? "Это выходной."
            : activeOrderWarning?.reason === "ordered"
              ? "На этот день уже создана заявка."
              : ""
          } Вы уверены, что хотите создать заказ?`}</Modal.Header>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setActiveOrderWarning(null);
              setActiveOrder(activeOrderWarning.activeOrder);
            }}
          >
            Создать заказ
          </Button>
          <Button
            onClick={() => {
              setActiveOrderWarning(null);
            }}
          >
            Отмена
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MyOrders;
